// Grouping for the "Brottstyper i {stad}" section.
// Order here defines render order in the UI. Each crime type from Polisens
// API is matched against the keywords (case-insensitive, startsWith) of each
// category in order. Anything that doesn't match falls into "Övrigt".

export type CrimeCategoryKey =
  | "vald"
  | "egendom"
  | "allmanfarliga"
  | "trafik"
  | "service"
  | "narkotika"
  | "ovrigt";

interface CategoryDef {
  key: CrimeCategoryKey;
  label: string;
  /** Match by lowercased startsWith (handles "Misshandel, grov" etc). */
  prefixes: string[];
}

export const CRIME_CATEGORIES: CategoryDef[] = [
  {
    key: "vald",
    label: "Vålds- & Personbrott",
    prefixes: [
      "misshandel",
      "sexualbrott",
      "våldtäkt",
      "rån väpnat",
      "rån",
      "mord",
      "dråp",
      "olaga hot",
      "våld/hot mot tjänsteman",
      "våld mot tjänsteman",
      "hot mot tjänsteman",
      "ofredande",
      "vållande till kroppsskada",
      "vållande till annans död",
      "människorov",
      "olaga frihetsberövande",
      "grov kvinnofridskränkning",
      "kvinnofridskränkning",
      "kränkande fotografering",
      "stalkning",
    ],
  },
  {
    key: "egendom",
    label: "Egendomsbrott & Stöld",
    prefixes: [
      "bedrägeri",
      "stöld",
      "stöld/inbrott",
      "inbrott",
      "häleri",
      "motorfordon",
      "olaga intrång",
      "hemfridsbrott",
      "skadegörelse",
      "klotter",
      "förskingring",
      "tillgrepp av fortskaffningsmedel",
      "penningtvätt",
    ],
  },
  {
    key: "allmanfarliga",
    label: "Allmänfarliga, Brand & Vapen",
    prefixes: [
      "mordbrand",
      "brand",
      "explosion",
      "detonation",
      "sprängning",
      "smällar",
      "skottlossning",
      "farligt föremål",
      "vapenlagen",
      "knivlagen",
      "skyddslagen",
      "sabotage",
      "allmänfarlig ödeläggelse",
      "allmänfarlig vårdslöshet",
      "framkallande av fara",
    ],
  },
  {
    key: "trafik",
    label: "Trafik & Olyckor",
    prefixes: [
      "trafikolycka",
      "rattfylleri",
      "drograttfylleri",
      "trafikbrott",
      "trafikkontroll",
      "trafikhinder",
      "olovlig körning",
      "smitning",
      "vårdslöshet i trafik",
      "luftfartslagen",
      "sjölagen",
    ],
  },
  {
    key: "service",
    label: "Service, Kontroll & Ordning",
    prefixes: [
      "larm överfall",
      "larm inbrott",
      "polisinsats",
      "kommendering",
      "kontroll",
      "kontroll person/fordon",
      "bråk",
      "fylleri",
      "lob",
      "anträffad död",
      "anträffad person",
      "räddningsinsats",
      "fjällräddning",
      "sjukdom",
      "sjukdom/olycksfall",
      "djur",
      "viltolycka",
      "försvunnen person",
      "efterlyst person",
      "information",
      "hittegods",
    ],
  },
  {
    key: "narkotika",
    label: "Narkotika & Alkohol",
    prefixes: [
      "narkotikabrott",
      "narkotika",
      "dopningsbrott",
      "alkohollagen",
      "smuggling",
    ],
  },
];

const OTHER: CategoryDef = {
  key: "ovrigt",
  label: "Övrigt",
  prefixes: [],
};

export function categorizeCrime(type: string): CrimeCategoryKey {
  const lower = type.toLowerCase().trim();
  for (const cat of CRIME_CATEGORIES) {
    for (const p of cat.prefixes) {
      if (lower === p || lower.startsWith(p)) return cat.key;
    }
  }
  return OTHER.key;
}

export function getCategoryLabel(key: CrimeCategoryKey): string {
  if (key === OTHER.key) return OTHER.label;
  return CRIME_CATEGORIES.find((c) => c.key === key)?.label ?? OTHER.label;
}

/**
 * Kort förklaring av vad en specifik polishändelse-typ faktiskt betyder.
 * Används för att skilja t.ex. "Trafikbrott" (anmält brott, ej olycka på plats)
 * från "Trafikolycka" (faktisk olycka rapporterad från platsen) — eftersom
 * användare ofta blandar ihop dem när de ser en pushnotis.
 * Returnerar null om typen inte behöver extra förklaring.
 */
export function crimeTypeDescription(type: string): string | null {
  const t = type.toLowerCase().trim();
  if (t === "trafikbrott")
    return "Anmält brott i trafiken — t.ex. fortkörning eller olovlig körning. Inte nödvändigtvis en olycka på platsen.";
  if (t.startsWith("trafikolycka"))
    return "Olycka rapporterad från platsen (kollision, singelolycka, viltolycka). Räknas som olyckshändelse om inte brottsmisstanke konstateras.";
  if (t === "rattfylleri") return "Misstänkt rattfylleri — förare påverkad av alkohol eller droger.";
  if (t === "trafikkontroll") return "Polisens trafikkontroll på platsen (ej olycka).";
  if (t === "trafikhinder") return "Hinder eller störning i trafiken (ej olycka).";
  if (t === "olovlig körning") return "Körning utan giltigt körkort.";
  if (t === "brand") return "Utryckning till brand. Kan röra sig om allt från olyckshändelse/självantändning till misstänkt mordbrand.";
  if (t === "explosion" || t === "detonation" || t === "sprängning") return "Rapporterad explosion eller detonation. Utreds ofta som allmänfarlig ödeläggelse.";
  if (t.startsWith("vållande till kroppsskada")) return "Lagöverträdelse enligt Brottsbalken 3 kap där oaktsamhet orsakat personskada.";
  if (t === "fylleri" || t === "lob") return "Omhändertagande enligt LOB (lagen om omhändertagande av berusade personer). Vårdande ordningsåtgärd, ej brott.";
  if (t === "skadegörelse") return "Uppsåtlig förstörelse eller skadegörelse på egendom (Brottsbalken 12 kap).";
  return null;
}

/** Render order including "Övrigt" at the end. */
export const CATEGORY_ORDER: CrimeCategoryKey[] = [
  ...CRIME_CATEGORIES.map((c) => c.key),
  OTHER.key,
];

export type EventCrimeStatus = "crime" | "non_crime" | "unclassified";

export interface ClassifiableEvent {
  type?: string | null;
  name?: string | null;
  summary?: string | null;
  body?: string | null;
  customTitle?: string | null;
}

// Inneboende brottstyper enligt svensk lag (om inte texten uttryckligen avfärdar brott)
const INHERENT_CRIME_PREFIXES = [
  "misshandel",
  "stöld",
  "inbrott",
  "rån",
  "narkotika",
  "sexualbrott",
  "våldtäkt",
  "mord",
  "dråp",
  "bedrägeri",
  "utpressning",
  "häleri",
  "förskingring",
  "skadegörelse",
  "klotter",
  "olaga hot",
  "våld/hot mot tjänsteman",
  "våld mot tjänsteman",
  "hot mot tjänsteman",
  "ofredande",
  "hemfridsbrott",
  "olaga intrång",
  "olaga frihetsberövande",
  "människorov",
  "kränkande fotografering",
  "stalkning",
  "vapenlagen",
  "knivlagen",
  "skyddslagen",
  "sabotage",
  "spioneri",
  "sprängämne",
  "skottlossning",
  "rattfylleri",
  "drograttfylleri",
  "olovlig körning",
  "smitning",
  "vårdslöshet i trafik",
  "trafikbrott",
  "mordbrand",
  "allmänfarlig ödeläggelse",
  "explosion",
  "detonation",
  "sprängning",
  "smällar",
  "farligt föremål",
  "vållande till kroppsskada",
  "vållande till annans död",
  "vållande",
  "arbetsmiljöbrott",
  "framkallande av fara",
  "missbruk av urkund",
  "urkundsförfalskning",
  "penningtvätt",
  "miljöbrott",
  "jaktbrott",
  "upplopp",
  "våldsamt upplopp",
  "alkohollagen",
  "motorfordon",
];

// Inneboende icke-brottsliga händelser, olyckor eller serviceinsatser
const INHERENT_NON_CRIME_PREFIXES = [
  "viltolycka",
  "fjällräddning",
  "räddningsinsats",
  "sjukdom",
  "sjukdom/olycksfall",
  "djur",
  "information",
  "försvunnen person",
  "anträffad person",
  "fylleri",
  "lob",
  "trafikkontroll",
  "kontroll",
  "kontroll person/fordon",
  "hittegods",
  "poliskommendering",
  "polisinsats/kommendering",
  "sammanfattning",
];

// Textmönster för uttryckligt ICKE-BROTT i polisens rapporter/artiklar
const NON_CRIME_TEXT_PATTERNS = [
  /ingen\s+misstanke\s+om\s+brott/i,
  /inga\s+misstankar\s+om\s+brott/i,
  /finns\s+(?:i\s+nuläget\s+)?ingen\s+misstanke/i,
  /finns\s+(?:i\s+nuläget\s+)?inga\s+misstankar/i,
  /inget\s+brott\s+misstänks/i,
  /inte\s+misstanke\s+om\s+brott/i,
  /ej\s+misstanke\s+om\s+brott/i,
  /ingen\s+misstanke\s+om\s+något\s+brott/i,
  /inga\s+brottsmisstankar/i,
  /ingen\s+brottsmisstanke/i,
  /inget\s+brott\s+föreligger/i,
  /misstänker\s+(?:inte|inget|ej)\s+(?:något\s+)?brott/i,
  /inget\s+brottsligt/i,
  /inte\s+något\s+brottsligt/i,
  /inte\s+brottsligt/i,
  /inte\s+begåtts\s+n[åa]g[o]?t\s+brott/i,
  /inget\s+brott\s+har\s+begåtts/i,
  /avskrivs\s+utan\s+misstanke/i,
  /avskrivet\s+utan\s+misstanke/i,
  /bedöms\s+som\s+(?:en\s+)?olyckshändelse/i,
  /bedöms\s+som\s+(?:en\s+)?olycka/i,
  /ren\s+olyckshändelse/i,
  /självantändning/i,
  /naturlig\s+död/i,
  /naturliga\s+orsaker/i,
  /sjukdomsfall/i,
  /falsklarm/i,
  /fellarm/i,
  /obefogat\s+larm/i,
  /larm(?:et)?\s+(?:utlösts?|gått)\s+av\s+misstag/i,
  /kontroll\s+utan\s+anmärkning/i,
  /utan\s+anmärkning/i,
  /inga\s+böter/i,
  /inga\s+anmärkningar/i,
  /blåste\s+negativt/i,
  /blåste\s+0[,.]00/i,
  /alla\s+(?:förare\s+)?var\s+nyktra/i,
  /ingen\s+anmälan\s+(?:om\s+brott\s+)?(?:upprättas|skrivs)/i,
  /endast\s+plåtskador\s+och\s+ingen\s+misstanke/i,
  /sammanfattningsvis\s+lugnt/i,
];

// Textmönster för bekräftat/misstänkt brott i texten
const CRIME_TEXT_PATTERNS = [
  /misstänks\s+för\s+(?:brott|mordbrand|allmänfarlig|vårdslöshet|rattfylleri|drograttfylleri|smitning|stöld|misshandel|olaga|skadegörelse|narkotika|häleri|olovlig|vållande)/i,
  /misstanke\s+om\s+(?:brott|mordbrand|allmänfarlig|vårdslöshet|stöld|misshandel|skadegörelse|narkotikabrott|grov|smitning|rattfylleri|vållande)/i,
  /misstanke\s+om\s+brott/i,
  /brottsmisstanke/i,
  /brottsrubricering/i,
  /allmänfarlig\s+ödeläggelse/i,
  /allmänfarlig\s+vårdslöshet/i,
  /mordbrand/i,
  /vårdslöshet\s+i\s+trafik/i,
  /vållande\s+till\s+kroppsskada/i,
  /vållande\s+till\s+annans\s+död/i,
  /smitning/i,
  /ej\s+anpassat\s+avstånd/i,
  /ej\s+iakttagit\s+väjningsplikt/i,
  /(?:upprättat|upprättas|upprättat\s+en|upprättar|skriver)\s+anmälan/i,
  /anmälan\s+(?:har\s+upprättats|är\s+upprättad|upprättas|skrivs|om\s+brott|om\s+mordbrand|om\s+skadegörelse|om\s+stöld|om\s+misshandel|om\s+vårdslöshet|om\s+smitning|om\s+allmänfarlig|om\s+vållande)/i,
  /anmälan\s+gällande/i,
  /förundersökning\s+(?:har\s+inletts|är\s+inledd|inledd|pågår)/i,
  /inlett\s+(?:en\s+)?förundersökning/i,
  /\b(?:gripen|gripits|greps|anhållen|anhållits|anhölls|häktad|häktats)\b/i,
  /misstänkt\s+(?:person|gärningsperson|gärningsman|förare)/i,
  /(?:beslagtagits|togs\s+i\s+beslag|tagits\s+i\s+beslag|tagit\s+i\s+beslag)/i,
  /(?:ordningsbot|böter)\s+(?:utfärdades|skrevs|gavs)/i,
  /delgavs\s+misstanke/i,
  /misstänks\s+vara\s+anlagd/i,
  /misstänker\s+att\s+branden\s+är\s+anlagd/i,
  /branden\s+är\s+anlagd/i,
  /anlagd\s+brand/i,
  /avspärrat\s+för\s+teknisk\s+undersökning/i,
  /kriminalteknisk\s+undersökning/i,
  /teknisk\s+undersökning\s+ska\s+genomföras/i,
];

/**
 * Analyserar text, rubriker och händelsetyp för att klassificera en händelse i:
 * 1 = "crime" (Garanterat / Misstänkt brott)
 * 2 = "non_crime" (Inget brott / Olyckor, kontroller, serviceinsatser eller uttrycklig icke-misstanke)
 * 3 = "unclassified" (Ej klassificerad / Oklart / Under pågående utredning)
 */
export function classifyEventCrimeStatus(event: ClassifiableEvent): EventCrimeStatus {
  const textParts: string[] = [];
  if (event.customTitle) textParts.push(event.customTitle);
  if (event.name) textParts.push(event.name);
  if (event.summary) textParts.push(event.summary);
  if (event.body) textParts.push(event.body);
  const fullText = textParts.join(" ").trim();

  // 1. Uttryckligt ICKE-BROTT i texten (t.ex. "ingen misstanke om brott", "avskrivs utan misstanke")
  for (const pattern of NON_CRIME_TEXT_PATTERNS) {
    if (pattern.test(fullText)) {
      return "non_crime";
    }
  }

  // 2. Uttryckligt BROTT i texten (t.ex. förundersökning inledd, gripen, anmälan upprättad, misstanke)
  for (const pattern of CRIME_TEXT_PATTERNS) {
    if (pattern.test(fullText)) {
      return "crime";
    }
  }

  const type = (event.type || "").toLowerCase().trim();

  // 3. Inneboende brottstyper (t.ex. Misshandel, Stöld, Inbrott, Rån, Explosion, Vållande till kroppsskada)
  for (const p of INHERENT_CRIME_PREFIXES) {
    if (type === p || type.startsWith(p)) {
      return "crime";
    }
  }

  // 4. Inneboende icke-brottsliga händelser (t.ex. Viltolycka, Fjällräddning, Sjukdom, Kontroller, Poliskommendering)
  for (const p of INHERENT_NON_CRIME_PREFIXES) {
    if (type === p || type.startsWith(p)) {
      return "non_crime";
    }
  }

  // 5. Trafikolyckor och trafikhinder utan textträff: kan vara brott (t.ex. smitning,
  // vårdslöshet) men inte alltid — klassas därför som "Ej klassificerat" tills
  // texten visar brottsmisstanke (krav från produktbeslut).
  // 6. Bränder eller övrigt utan känd orsak eller rubricering i rapporten
  return "unclassified";
}

/**
 * Klassificering baserad enbart på typnamn (om text saknas)
 */
export function classifyTypeOnly(type: string): EventCrimeStatus {
  const t = type.toLowerCase().trim();
  for (const p of INHERENT_CRIME_PREFIXES) {
    if (t === p || t.startsWith(p)) return "crime";
  }
  for (const p of INHERENT_NON_CRIME_PREFIXES) {
    if (t === p || t.startsWith(p)) return "non_crime";
  }
  // Trafikolycka/trafikhinder klassas som okänt utan textstöd (kan vara brott).
  return "unclassified";
}

/**
 * Bakåtkompatibel funktion för att kontrollera om en händelsetyp är ett brott
 */
export function isSuspectedCrime(type: string): boolean {
  return classifyTypeOnly(type) === "crime";
}

export interface NatureTypeCount {
  type: string;
  count: number;
  pct: number;
}

export interface CrimeNatureBreakdown {
  total: number;
  // 1 = Misstänkta / Bekräftade brott
  crimeCount: number;
  crimePct: number;
  topCrimes: NatureTypeCount[];
  // 2 = Inget brott (olyckor, kontroller, ingen misstanke)
  incidentCount: number;
  incidentPct: number;
  topIncidents: NatureTypeCount[];
  // 3 = Ej klassificerad (oklart / under utredning)
  unclassifiedCount: number;
  unclassifiedPct: number;
  topUnclassified: NatureTypeCount[];
}

export function computeCrimeNatureBreakdown(
  breakdown?: Array<{ type: string; count: number }>,
  events?: ClassifiableEvent[],
): CrimeNatureBreakdown {
  const crimeMap = new Map<string, number>();
  const incidentMap = new Map<string, number>();
  const unclassifiedMap = new Map<string, number>();

  let crimeCount = 0;
  let incidentCount = 0;
  let unclassifiedCount = 0;

  if (events && events.length > 0) {
    for (const ev of events) {
      const type = (ev.type || "Övrigt").trim();
      const status = classifyEventCrimeStatus(ev);
      if (status === "crime") {
        crimeCount++;
        crimeMap.set(type, (crimeMap.get(type) || 0) + 1);
      } else if (status === "non_crime") {
        incidentCount++;
        incidentMap.set(type, (incidentMap.get(type) || 0) + 1);
      } else {
        unclassifiedCount++;
        unclassifiedMap.set(type, (unclassifiedMap.get(type) || 0) + 1);
      }
    }
  } else if (breakdown && breakdown.length > 0) {
    for (const item of breakdown) {
      const status = classifyTypeOnly(item.type);
      if (status === "crime") {
        crimeCount += item.count;
        crimeMap.set(item.type, (crimeMap.get(item.type) || 0) + item.count);
      } else if (status === "non_crime") {
        incidentCount += item.count;
        incidentMap.set(item.type, (incidentMap.get(item.type) || 0) + item.count);
      } else {
        unclassifiedCount += item.count;
        unclassifiedMap.set(item.type, (unclassifiedMap.get(item.type) || 0) + item.count);
      }
    }
  }

  const total = crimeCount + incidentCount + unclassifiedCount;

  const toList = (map: Map<string, number>): NatureTypeCount[] => {
    return Array.from(map.entries())
      .map(([type, count]) => ({
        type,
        count,
        pct: total > 0 ? Math.round((count / total) * 1000) / 10 : 0,
      }))
      .sort((a, b) => b.count - a.count);
  };

  const topCrimes = toList(crimeMap);
  const topIncidents = toList(incidentMap);
  const topUnclassified = toList(unclassifiedMap);

  let crimePct = total > 0 ? Math.round((crimeCount / total) * 100) : 0;
  let incidentPct = total > 0 ? Math.round((incidentCount / total) * 100) : 0;
  let unclassifiedPct = total > 0 ? Math.round((unclassifiedCount / total) * 100) : 0;

  if (total > 0 && crimePct + incidentPct + unclassifiedPct !== 100) {
    const diff = 100 - (crimePct + incidentPct + unclassifiedPct);
    if (crimeCount >= incidentCount && crimeCount >= unclassifiedCount) {
      crimePct += diff;
    } else if (incidentCount >= crimeCount && incidentCount >= unclassifiedCount) {
      incidentPct += diff;
    } else {
      unclassifiedPct += diff;
    }
  }

  return {
    total,
    crimeCount,
    crimePct,
    topCrimes,
    incidentCount,
    incidentPct,
    topIncidents,
    unclassifiedCount,
    unclassifiedPct,
    topUnclassified,
  };
}